'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@common/components';
import SvgFourLeafClover from '@common/icon/SvgFourLeafClover';
import { cn } from '@core/utils';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { type AuthApi, AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { ROUTES } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useHaptic } from '@/hooks/useHaptic';
import { useIsGuest } from '@/hooks/useIsGuest';
import { useAdGuards } from '@/modules/adpopcorn/_hooks/useAdGuards';
import { postBenefitQuickMenuClick } from '@/utils/analytics/welloTrack';
import { useAffiliateClick } from '../_hooks/useAffiliateClick';
import { useAutoAttendanceCheckIn } from '../_hooks/useAutoAttendanceCheckIn';
import { useBenefitGate } from '../_hooks/useBenefitGate';
import { useBenefitMenus } from '../_hooks/useBenefitMenus';
import { useCountdown } from '../_hooks/useCountdown';
import { useOpenOfferwall } from '../_hooks/useOpenOfferwall';
import { formatCountdownHoursMinutes, parseKstDateToMs } from '../_utils/countdown';
import { resolveQuickMenuTiles } from '../_utils/resolveQuickMenuTiles';
import { resolveVisitLuckyTicketTile } from '../_utils/resolveVisitLuckyTicket';
import { useAdBonusReward } from '../lucky-ticket/_hooks/useAdBonusReward';
import { useBonusSlot } from '../lucky-ticket/_hooks/useBonusSlot';

//* 모바일 하단 여백은 아래 탭 카드의 둥근 윗변이 회색 띠 위에 얹히는 자리다.
const SECTION_CLASS_NAME = 'bg-Gray-25 px-20 pb-32 pt-20 xl:rounded-large xl:border xl:border-Gray-100 xl:bg-Gray-white xl:p-20';

//* 칸 수와 무관하게 5칸 자리에 왼쪽부터 채운다. 좁은 화면은 가로 스크롤, 상하 여백은 배지 자리.
const TILE_ROW_CLASS_NAME = '-mx-20 -my-8 grid grid-cols-[repeat(5,minmax(66px,1fr))] justify-items-center overflow-x-auto px-20 py-8 hide-scrollbar webkit-scroll-touch tablet:flex tablet:gap-12 xl:mx-0 xl:flex xl:justify-center xl:gap-12 xl:px-0';
const TILE_CLASS_NAME = 'group relative flex shrink-0 flex-col items-center gap-8 px-8';
const ICON_BOX_CLASS_NAME = 'relative flex size-50 items-center justify-center overflow-hidden rounded-large border transition-colors';
const OFFERWALL_ICON_BOX_CLASS_NAME = 'border-Y-400 bg-Y-300 group-focus-visible:bg-Y-400 group-active:bg-Y-400 group-hover:bg-Y-400';
const DEFAULT_ICON_BOX_CLASS_NAME = 'border-Gray-200 bg-Gray-white group-hover:border-Gray-300 group-focus-visible:border-Gray-300 group-active:border-Gray-300';
const TILE_LABEL_CLASS_NAME = 'whitespace-nowrap font-600 text-Gray-900 typo-caption1';
const COOLDOWN_PILL_CLASS_NAME = 'absolute inset-x-0 top-30 mx-auto flex h-28 w-fit items-center rounded-pill border border-Gray-white bg-Gray-black px-8 font-600 tabular-nums text-Gray-white typo-caption1';
const CLOVER_BADGE_CLASS_NAME = 'absolute -top-6 right-4 flex w-26 items-center justify-center rounded-pill border border-Gray-50 bg-Gray-white py-2';
type VisitLuckyTicketMerchant = NonNullable<NonNullable<NonNullable<Awaited<ReturnType<(typeof AuthApi)['get:/wello/v1/auth/affiliate/merchants/lucky-ticket']>>['data']>['context']>['contents']>[number] & {
  merchantCode: string;
};
interface VisitLuckyTicketMerchantTileProps {
  merchant: VisitLuckyTicketMerchant;
  isDailyCapReached: boolean;
  isLuckyTicketActive: boolean;
}
export const VisitLuckyTicketMerchantTile = t0 => {
  const $ = _c(40);
  const {
    merchant,
    isDailyCapReached,
    isLuckyTicketActive
  } = t0;
  const queryClient = useQueryClient();
  const isReceivable = merchant.receivableYN === true;
  let t1;
  if ($[0] !== isReceivable || $[1] !== merchant.nextAvailableAt) {
    t1 = !isReceivable && merchant.nextAvailableAt ? parseKstDateToMs(merchant.nextAvailableAt) : null;
    $[0] = isReceivable;
    $[1] = merchant.nextAvailableAt;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const nextAvailableAtMs = t1;
  const countdown = useCountdown(nextAvailableAtMs);
  const isCooldownElapsed = nextAvailableAtMs !== null && countdown.isAvailable;
  let t2;
  let t3;
  if ($[3] !== isCooldownElapsed || $[4] !== queryClient) {
    t2 = () => {
      if (isCooldownElapsed) {
        queryClient.invalidateQueries({
          queryKey: AuthQueryOptions["get:/wello/v1/auth/affiliate/merchants/lucky-ticket"]().queryKey
        });
      }
    };
    t3 = [isCooldownElapsed, queryClient];
    $[3] = isCooldownElapsed;
    $[4] = queryClient;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const {
    isBadgeActive,
    cooldownLabel
  } = resolveVisitLuckyTicketTile({
    isReceivable,
    isDailyCapReached,
    countdown
  });
  const merchantName = merchant.merchantName ?? "";
  const {
    startAffiliateClick,
    isPending
  } = useAffiliateClick();
  const {
    triggerHaptic
  } = useHaptic();
  const {
    isWelloApp
  } = useCheckWelloApp();
  let t4;
  if ($[7] !== isWelloApp || $[8] !== merchant.channelType || $[9] !== merchant.logoImageUrl || $[10] !== merchant.merchantCode || $[11] !== merchant.openMode || $[12] !== merchantName || $[13] !== startAffiliateClick || $[14] !== triggerHaptic) {
    t4 = () => {
      triggerHaptic();
      postBenefitQuickMenuClick({
        tileType: "MERCHANT",
        merchantCode: merchant.merchantCode,
        appYN: isWelloApp
      });
      startAffiliateClick({
        target: {
          type: "merchant"
        },
        entryPointType: isWelloApp ? "QUICK_MENU" : "MERCHANT_DETAIL",
        appGuideModalPolicy: undefined,
        openMode: merchant.openMode,
        channelType: merchant.channelType,
        guide: {
          merchantCode: merchant.merchantCode,
          merchantName,
          logoImageUrl: merchant.logoImageUrl ?? "",
          showDetailLink: false
        }
      });
    };
    $[7] = isWelloApp;
    $[8] = merchant.channelType;
    $[9] = merchant.logoImageUrl;
    $[10] = merchant.merchantCode;
    $[11] = merchant.openMode;
    $[12] = merchantName;
    $[13] = startAffiliateClick;
    $[14] = triggerHaptic;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleClick = t4;
  const t5 = cooldownLabel ? `${merchantName}, 행운권 ${cooldownLabel} 남음` : undefined;
  const t6 = TILE_CLASS_NAME;
  const t7 = cn(ICON_BOX_CLASS_NAME, DEFAULT_ICON_BOX_CLASS_NAME, cooldownLabel && "opacity-30");
  let t8;
  if ($[16] !== merchant.logoImageUrl) {
    t8 = <Image src={merchant.logoImageUrl} alt="" width={50} height={50} sizes="50px" className="relative size-full bg-Gray-white object-contain p-4" />;
    $[16] = merchant.logoImageUrl;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <span className={t7}>{t8}</span>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== cooldownLabel) {
    t10 = cooldownLabel ? <span className={COOLDOWN_PILL_CLASS_NAME}>{cooldownLabel}</span> : null;
    $[21] = cooldownLabel;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = cooldownLabel && "opacity-30";
  let t12;
  if ($[23] !== t11) {
    t12 = cn(TILE_LABEL_CLASS_NAME, "max-w-64 truncate", t11);
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== merchantName || $[26] !== t12) {
    t13 = <span className={t12}>{merchantName}</span>;
    $[25] = merchantName;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== isBadgeActive || $[29] !== isLuckyTicketActive) {
    t14 = isLuckyTicketActive ? <span aria-hidden={true} className={CLOVER_BADGE_CLASS_NAME}><SvgFourLeafClover originalFill={true} className={cn("size-16", !isBadgeActive && "opacity-50 grayscale")} /></span> : null;
    $[28] = isBadgeActive;
    $[29] = isLuckyTicketActive;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== handleClick || $[32] !== isPending || $[33] !== t10 || $[34] !== t13 || $[35] !== t14 || $[36] !== t5 || $[37] !== t6 || $[38] !== t9) {
    t15 = <button disabled={isPending} onClick={handleClick} aria-label={t5} className={t6}>{t9}{t10}{t13}{t14}</button>;
    $[31] = handleClick;
    $[32] = isPending;
    $[33] = t10;
    $[34] = t13;
    $[35] = t14;
    $[36] = t5;
    $[37] = t6;
    $[38] = t9;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
};

//* 비회원 목록은 쿨다운 없이 전 몰이 받을 수 있음으로 오고, 탭하면 클릭 훅이 로그인으로 보낸다.
interface VisitLuckyTicketMerchantTilesProps {
  isGuest: boolean;
  tileCount: number;
}
const VisitLuckyTicketMerchantTiles = t0 => {
  const $ = _c(12);
  const {
    isGuest,
    tileCount
  } = t0;
  let t1;
  if ($[0] !== isGuest) {
    t1 = isGuest ? UnauthQueryOptions["get:/wello/v1/allows/affiliate/merchants/lucky-ticket"]() : AuthQueryOptions["get:/wello/v1/auth/affiliate/merchants/lucky-ticket"]();
    $[0] = isGuest;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...t1,
      select: _temp2
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data
  } = useSuspenseQuery(t2);
  let t3;
  if ($[4] !== data.isDailyCapReached || $[5] !== data.isLuckyTicketActive || $[6] !== data.merchants || $[7] !== tileCount) {
    let t4;
    if ($[9] !== data.isDailyCapReached || $[10] !== data.isLuckyTicketActive) {
      t4 = merchant_0 => <VisitLuckyTicketMerchantTile key={merchant_0.merchantCode} merchant={merchant_0} isDailyCapReached={data.isDailyCapReached} isLuckyTicketActive={data.isLuckyTicketActive} />;
      $[9] = data.isDailyCapReached;
      $[10] = data.isLuckyTicketActive;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = data.merchants.slice(0, tileCount).map(t4);
    $[4] = data.isDailyCapReached;
    $[5] = data.isLuckyTicketActive;
    $[6] = data.merchants;
    $[7] = tileCount;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
interface LuckyTicketAdButtonProps {
  title: string;
  imageUrl?: string;
  onClick: () => void;
  isBusy: boolean;
  cooldownLabel?: string;
}
const LuckyTicketAdButton = t0 => {
  const $ = _c(22);
  const {
    title,
    imageUrl,
    onClick,
    isBusy,
    cooldownLabel
  } = t0;
  const t1 = isBusy || Boolean(cooldownLabel);
  const t2 = cooldownLabel ? `${title}, ${cooldownLabel} 남음` : undefined;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(TILE_CLASS_NAME, "disabled:cursor-not-allowed");
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const t4 = (cooldownLabel || isBusy) && "opacity-30";
  let t5;
  if ($[1] !== t4) {
    t5 = cn(ICON_BOX_CLASS_NAME, DEFAULT_ICON_BOX_CLASS_NAME, t4);
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== imageUrl) {
    t6 = <Image src={imageUrl} alt="" width={40} height={40} sizes="40px" className="relative size-30 shrink-0 object-contain" />;
    $[3] = imageUrl;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t5 || $[6] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== cooldownLabel) {
    t8 = cooldownLabel ? <span className={COOLDOWN_PILL_CLASS_NAME}>{cooldownLabel}</span> : null;
    $[8] = cooldownLabel;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = (cooldownLabel || isBusy) && "opacity-30";
  let t10;
  if ($[10] !== t9) {
    t10 = cn(TILE_LABEL_CLASS_NAME, t9);
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== title) {
    t11 = <span className={t10}>{title}</span>;
    $[12] = t10;
    $[13] = title;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== onClick || $[16] !== t1 || $[17] !== t11 || $[18] !== t2 || $[19] !== t7 || $[20] !== t8) {
    t12 = <button onClick={onClick} disabled={t1} aria-label={t2} className={t3}>{t7}{t8}{t11}</button>;
    $[15] = onClick;
    $[16] = t1;
    $[17] = t11;
    $[18] = t2;
    $[19] = t7;
    $[20] = t8;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
};
const LuckyTicketAdCooldownButton = props => {
  const $ = _c(6);
  const {
    data
  } = useBonusSlot();
  const countdown = useCountdown(data.nextAvailableAtMs);
  let t0;
  if ($[0] !== countdown.isAvailable || $[1] !== countdown.secondsUntilAvailable) {
    t0 = countdown.isAvailable ? undefined : formatCountdownHoursMinutes(countdown.secondsUntilAvailable);
    $[0] = countdown.isAvailable;
    $[1] = countdown.secondsUntilAvailable;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== props || $[4] !== t0) {
    t1 = <LuckyTicketAdButton {...props} cooldownLabel={t0} />;
    $[3] = props;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
interface LuckyTicketAdTileProps {
  benefitMenuId?: string;
  title: string;
  imageUrl?: string;
  isGuest: boolean;
}

//* 로또 카드의 광고 행운권 보너스를 그대로 쓴다. 비회원은 auth 쿼리를 태우지 않고, 게이트가 로그인으로 보낸다.
const LuckyTicketAdTile = t0 => {
  const $ = _c(16);
  const {
    benefitMenuId,
    title,
    imageUrl,
    isGuest
  } = t0;
  const {
    triggerGate,
    gateModals
  } = useBenefitGate();
  const {
    watchAd,
    phase,
    isRewarding
  } = useAdBonusReward();
  const {
    isWelloApp
  } = useAdGuards();
  let t1;
  if ($[0] !== benefitMenuId || $[1] !== isWelloApp || $[2] !== triggerGate || $[3] !== watchAd) {
    t1 = () => {
      if (benefitMenuId) {
        postBenefitQuickMenuClick({
          tileType: "BENEFIT_MENU",
          benefitMenuId,
          appYN: isWelloApp
        });
      }
      triggerGate(["app", "login", "adult", "consent"], watchAd);
    };
    $[0] = benefitMenuId;
    $[1] = isWelloApp;
    $[2] = triggerGate;
    $[3] = watchAd;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = phase === "pending" || phase === "cooldown" || isRewarding;
  let t3;
  if ($[5] !== imageUrl || $[6] !== t1 || $[7] !== t2 || $[8] !== title) {
    t3 = {
      title,
      imageUrl,
      onClick: t1,
      isBusy: t2
    };
    $[5] = imageUrl;
    $[6] = t1;
    $[7] = t2;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const buttonProps = t3;
  let t4;
  if ($[10] !== buttonProps || $[11] !== isGuest) {
    t4 = isGuest ? <LuckyTicketAdButton {...buttonProps} /> : <ErrorBoundary fallback={<LuckyTicketAdButton {...buttonProps} />}><Suspense fallback={<LuckyTicketAdButton {...buttonProps} />}><LuckyTicketAdCooldownButton {...buttonProps} /></Suspense></ErrorBoundary>;
    $[10] = buttonProps;
    $[11] = isGuest;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== gateModals || $[14] !== t4) {
    t5 = <>{t4}{gateModals}</>;
    $[13] = gateModals;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
interface BenefitQuickActionSectionProps {
  isGuest: boolean;
}

/** 혜택 메뉴 첫 그룹의 퀵액션 타일(Figma 10422-109204). */
export const BenefitQuickActionSection = t0 => {
  const $ = _c(35);
  const {
    isGuest: serverIsGuest
  } = t0;
  const isGuest = useIsGuest(serverIsGuest);
  const {
    data: groups
  } = useBenefitMenus(isGuest);
  const {
    openOfferwall,
    gateModals
  } = useOpenOfferwall();
  const {
    isWelloApp
  } = useAdGuards();
  const t1 = !isGuest;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      enabled: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    isCheckedInToday
  } = useAutoAttendanceCheckIn(t2);
  const [quickActionGroup] = groups;
  if (!quickActionGroup) {
    return null;
  }
  let adItem;
  let merchantTileCount;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[2] !== isCheckedInToday || $[3] !== isWelloApp || $[4] !== openOfferwall || $[5] !== quickActionGroup.items || $[6] !== quickActionGroup.title) {
    const {
      menuItems,
      adItem: t7,
      merchantTileCount: t8
    } = resolveQuickMenuTiles(quickActionGroup.items, isWelloApp);
    adItem = t7;
    merchantTileCount = t8;
    t5 = quickActionGroup.title;
    t6 = SECTION_CLASS_NAME;
    t3 = TILE_ROW_CLASS_NAME;
    let t9;
    if ($[13] !== isCheckedInToday || $[14] !== isWelloApp || $[15] !== openOfferwall) {
      t9 = item => {
        const {
          url,
          placementKey
        } = item;
        const isOfferwall = !url && Boolean(placementKey);
        const isAttendanceTile = url === ROUTES.BENEFIT_ATTENDANCE.pathname;
        const trackClick = () => {
          if (item.benefitMenuId) {
            postBenefitQuickMenuClick({
              tileType: "BENEFIT_MENU",
              benefitMenuId: item.benefitMenuId,
              appYN: isWelloApp
            });
          }
        };
        const content = <><span className={cn(ICON_BOX_CLASS_NAME, isOfferwall ? OFFERWALL_ICON_BOX_CLASS_NAME : DEFAULT_ICON_BOX_CLASS_NAME)}><Image src={item.imageUrl} alt="" width={40} height={40} sizes="40px" className={cn("relative shrink-0 object-contain", isOfferwall ? "size-40" : "size-30")} /></span><span className={TILE_LABEL_CLASS_NAME}>{isAttendanceTile && isCheckedInToday ? "\uCD9C\uC11D \uC644\uB8CC" : item.title}</span></>;
        if (url) {
          return <CustomLink key={item.title} href={url} onClick={trackClick} className={TILE_CLASS_NAME}>{content}</CustomLink>;
        }
        if (placementKey) {
          return <button key={item.title} onClick={() => {
            trackClick();
            openOfferwall(placementKey);
          }} className={TILE_CLASS_NAME}>{content}</button>;
        }
        return <span key={item.title} aria-disabled="true" className={cn(TILE_CLASS_NAME, "opacity-50")}>{content}</span>;
      };
      $[13] = isCheckedInToday;
      $[14] = isWelloApp;
      $[15] = openOfferwall;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    t4 = menuItems.map(t9);
    $[2] = isCheckedInToday;
    $[3] = isWelloApp;
    $[4] = openOfferwall;
    $[5] = quickActionGroup.items;
    $[6] = quickActionGroup.title;
    $[7] = adItem;
    $[8] = merchantTileCount;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    adItem = $[7];
    merchantTileCount = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[17] !== isGuest || $[18] !== merchantTileCount) {
    t7 = <ErrorBoundary fallback={null}><Suspense fallback={null}><VisitLuckyTicketMerchantTiles isGuest={isGuest} tileCount={merchantTileCount} /></Suspense></ErrorBoundary>;
    $[17] = isGuest;
    $[18] = merchantTileCount;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== adItem || $[21] !== isGuest) {
    t8 = adItem ? <LuckyTicketAdTile benefitMenuId={adItem.benefitMenuId} title={adItem.title} imageUrl={adItem.imageUrl} isGuest={isGuest} /> : null;
    $[20] = adItem;
    $[21] = isGuest;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t3 || $[24] !== t4 || $[25] !== t7 || $[26] !== t8) {
    t9 = <div className={t3}>{t4}{t7}{t8}</div>;
    $[23] = t3;
    $[24] = t4;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t5 || $[29] !== t6 || $[30] !== t9) {
    t10 = <section aria-label={t5} className={t6}>{t9}</section>;
    $[28] = t5;
    $[29] = t6;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== gateModals || $[33] !== t10) {
    t11 = <>{t10}{gateModals}</>;
    $[32] = gateModals;
    $[33] = t10;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
};
export const BenefitQuickActionSectionFallback = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <section className={SECTION_CLASS_NAME}><div className={TILE_ROW_CLASS_NAME}>{[0, 1, 2, 3, 4].map(_temp3)}</div></section>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(t0) {
  const {
    merchantCode,
    ...merchant
  } = t0;
  return merchantCode ? [{
    ...merchant,
    merchantCode
  }] : [];
}
function _temp2(response) {
  return {
    isDailyCapReached: response?.context?.dailyCapReachedYN === true,
    isLuckyTicketActive: response?.context?.luckyTicketActiveYN === true,
    merchants: (response?.context?.contents ?? []).flatMap(_temp)
  };
}
function _temp3(tile) {
  return <div key={tile} className={TILE_CLASS_NAME}><Skeleton className="size-50 rounded-large" /><Skeleton typo="caption1" className="w-42 rounded-xsmall" /></div>;
}